import React from "react";
import { Typography, useTheme } from "@mui/material";
import LayoutBox from "./layout-box";
import { tokens } from "../layout/theme";

/**
 * Interface defining the props for the InfoField component.
 * @property {string} label - The label text to display before the value
 * @property {string | number | JSX.Element} [value] - The value to display
 * @property {string} [defaultValue="N/A"] - The default value to display when value is undefined
 * @property {string} [textColor] - Custom color for the value text
 * @property {boolean} [blur] - Whether to apply blur effect and prevent copying of the value
 */
interface InfoFieldProps {
  label: string;
  value?: string | number | JSX.Element;
  defaultValue?: string;
  textColor?: string;
  blur?: boolean;
}

/**
 * A reusable information field component that displays a label-value pair.
 * 
 * This component provides a styled layout for displaying information with:
 * - Label and value on the same line
 * - Customizable text colors
 * - Default value handling
 * - Text overflow handling
 * - Responsive text wrapping
 * 
 * @component
 * @param {InfoFieldProps} props - The props passed to the component
 * @param {string} props.label - The label text
 * @param {string | number | JSX.Element} [props.value] - The value to display
 * @param {string} [props.defaultValue="N/A"] - Default value when undefined
 * @param {string} [props.textColor] - Custom text color
 * 
 * @example
 * ```tsx
 * <InfoField
 *   label="Status"
 *   value="Active"
 *   defaultValue="Unknown"
 *   textColor="#00ff00"
 * />
 * ```
 * 
 * @remarks
 * - Uses Material-UI Typography components
 * - Supports string, number, and JSX element values
 * - Handles text overflow with ellipsis
 * - Supports word breaking for long content
 * - Maintains consistent spacing with padding
 * - Uses theme-based colors by default
 * 
 * @returns {JSX.Element} A React component that renders an information field
 */
const InfoField = (props: InfoFieldProps) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { label, value, defaultValue = "N/A", textColor, blur } = props; // Use defaultValue or "N/A" if value is undefined
  return (
    <>
      <LayoutBox>
        <Typography
          display="inline"
          variant="h5"
          color={colors.blueAccent[500]}
        >
          {label}:
        </Typography>
        <Typography
          display="inline"
          variant="h5"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "normal", // Allow text to wrap on all screen sizes
            wordBreak: "break-word", // Break long words on all screen sizes
            maxWidth: "100%", // Use 100% width of the container for all screen sizes
            paddingX: "10px",
            ...(blur && { 
              filter: "blur(5px)",
              userSelect: "none", // Prevent text selection
              pointerEvents: "none", // Disable mouse interactions
              WebkitUserSelect: "none", // Safari support
              MozUserSelect: "none", // Firefox support
              msUserSelect: "none", // IE support
            }), // Conditional blur and copy prevention
          }}
          color={textColor ? textColor : colors.blueAccent[100]}
        >
          {value !== undefined ? value : defaultValue}
        </Typography>
      </LayoutBox>
    </>
  );
};

export default InfoField;
